/**
 * @file apps/web/src/App.tsx
 * @description Root application component with routing.
 * Two layout trees:
 *   - AppShell (SI firm workspace) — sidebar layout, auth via useAuth()
 *   - PortalShell (client portal)  — topbar layout, auth via usePortalAuth()
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './components/AppShell.js';
import { PortalShell } from './components/PortalShell.js';

// SI firm workspace pages
import { DashboardPage }       from './pages/DashboardPage.js';
import { ProjectsPage }        from './pages/ProjectsPage.js';
import { ProjectDetailPage }   from './pages/ProjectDetailPage.js';
import { DiscoveryPage }       from './pages/DiscoveryPage.js';
import { DesignPage }          from './pages/DesignPage.js';
import { BuildPage }           from './pages/BuildPage.js';
import { UatPage }             from './pages/UatPage.js';
import { DocumentsPage }       from './pages/DocumentsPage.js';
import { ApprovalsPage }       from './pages/ApprovalsPage.js';
import { ApprovalsInboxPage }   from './pages/ApprovalsInboxPage.js';
import { SessionDetailPage }   from './pages/SessionDetailPage.js';
import { TrackPage }           from './pages/TrackPage.js';
import { ClosePage }           from './pages/ClosePage.js';
import { LoginPage }           from './pages/LoginPage.js';
import { AcceptInvitePage }    from './pages/AcceptInvitePage.js';
import { ProjectMembersPage }  from './pages/ProjectMembersPage.js';
import { UsersSettingsPage }   from './pages/UsersSettingsPage.js';
import { RolesSettingsPage }   from './pages/RolesSettingsPage.js';
import { SettingsPage }        from './pages/SettingsPage.js';
import { NotFoundPage }        from './pages/NotFoundPage.js';

// Client portal pages
import { PortalLoginPage }     from './pages/portal/PortalLoginPage.js';
import { PortalDashboardPage } from './pages/portal/PortalDashboardPage.js';
import { PortalScriptsPage }   from './pages/portal/PortalScriptsPage.js';
import { PortalFDDPage }       from './pages/portal/PortalFDDPage.js';
import { PortalApprovalsPage } from './pages/portal/PortalApprovalsPage.js';
import { PortalSignoffPage }   from './pages/portal/PortalSignoffPage.js';

export function App() {
  return (
    <Routes>
      {/* ---------------------------------------------------------------- */}
      {/* Public routes                                                    */}
      {/* ---------------------------------------------------------------- */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />

      {/* ---------------------------------------------------------------- */}
      {/* SI firm workspace — AppShell layout                             */}
      {/* ---------------------------------------------------------------- */}
      <Route element={<AppShell />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Projects */}
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />

        {/* Project sub-pages */}
        <Route path="/projects/:projectId/discovery"    element={<DiscoveryPage />} />
        <Route path="/projects/:projectId/design"       element={<DesignPage />} />
        <Route path="/projects/:projectId/build"        element={<BuildPage />} />
        <Route path="/projects/:projectId/uat"          element={<UatPage />} />
        <Route path="/projects/:projectId/documents"    element={<DocumentsPage />} />
        <Route path="/projects/:projectId/approvals"    element={<ApprovalsPage />} />
        <Route path="/projects/:projectId/members"      element={<ProjectMembersPage />} />
        <Route path="/approvals/inbox" element={<ApprovalsInboxPage />} />
        <Route path="/projects/:projectId/track"        element={<TrackPage />} />
        <Route path="/projects/:projectId/close"        element={<ClosePage />} />

        {/* Session detail (cross-project, by session ID) */}
        <Route path="/sessions/:sessionId" element={<SessionDetailPage />} />

        {/* Settings */}
        <Route path="/settings"        element={<SettingsPage />} />
        <Route path="/settings/users"  element={<UsersSettingsPage />} />
        <Route path="/settings/roles"  element={<RolesSettingsPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* ---------------------------------------------------------------- */}
      {/* Client portal — PortalShell layout                             */}
      {/* ---------------------------------------------------------------- */}

      {/* Portal login is outside PortalShell (no redirect guard) */}
      <Route path="/portal/login" element={<PortalLoginPage />} />

      {/* Protected portal routes — PortalShell redirects to /portal/login if not authed */}
      <Route element={<PortalShell />}>
        <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />
        <Route path="/portal/dashboard"  element={<PortalDashboardPage />} />
        <Route path="/portal/scripts"    element={<PortalScriptsPage />} />
        <Route path="/portal/fdd"        element={<PortalFDDPage />} />
        <Route path="/portal/approvals"  element={<PortalApprovalsPage />} />
        <Route path="/portal/signoff"    element={<PortalSignoffPage />} />
      </Route>
    </Routes>
  );
}
